import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useDataFeedWizardStore,
  useOTCItems,
} from '../../../../store/dataFeedWizard';
import {
  OTCInstrumentItem,
  MappingItem,
} from '../../../../types/generatedTypes';
import Actions from '../../../../store/Actions';
import { useTableSearch } from '../../../../hooks/useTableSearch';
import { useDisabledRows } from '../../hooks/useDisabledRows';
import { useInstrumentPrice } from '../../hooks/useInstrumentPrice';
import { coerceValue } from '../../utils/valueCoercion';

export const useLinkOTCStep = () => {
  const {
    configureMappingData,
    setInstrumentMappings,
    setOTCMappings,
    updateItemMapping,
    setCurrentStepButtonState,
    registerStepActions,
    clearStepActions,
    sessionId,
    setStepLoading,
    setPristineState,
    isMoving,
    setIsMoving,
  } = useDataFeedWizardStore();

  // Get selected OTC columns from the Configure Mapping step
  const otcColumns = configureMappingData.otcColumns
    .filter(col => col.column.trim() !== '')
    .map(col => col.column);

  // Get OTC items using stable selector hook (prevents infinite re-renders)
  const otcItems = useOTCItems();

  const { isItemDisabledAccountOnly } = useDisabledRows();
  const { prices, fetchPrice, fetchPricesForItems, clearPrice } = useInstrumentPrice();

  const [illioOTCOptions, setIllioOTCOptions] = useState<OTCInstrumentItem[]>(
    []
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Load OTC matches and available OTC instruments - ALWAYS reloads on mount (no caching)
  const loadOTCData = useCallback(async () => {
    if (!sessionId) {
      console.warn('Missing sessionId - cannot load OTC data');
      return;
    }

    setStepLoading(true);
    try {
      // Load both OTC matches and full OTC list in parallel
      const [matchResponse, otcListResponse] = await Promise.all([
        Actions.matchWizardOTC(sessionId),
        Actions.getWizardOTCInstruments(),
      ]);

      // Convert API response to store format (no category field needed)
      const otcMappingItems: MappingItem[] = matchResponse.otcInstruments.map(
        otc => ({
          id: otc.id,
          isin: otc.isin,
          symbol: otc.symbol,
          otc_values: otc.otc_values,
          instrument_source_id: otc.instrument_source_id,
          illio_code: otc.illio_code,
          name: otc.name,
          currency_code: otc.currency_code,
          label: otc.label,
          multiplier: otc.multiplier,
          original_multiplier: otc.original_multiplier,
          options: otc.options,
          instrument_type: otc.instrument_type,
          status: otc.status,
          otc_db_id: otc.otc_db_id,
          account_numbers: otc.account_numbers,
        })
      );

      // Set OTC items directly
      setOTCMappings(otcMappingItems);
      // Set pristine to freshly loaded data - runs every time since this step always reloads
      setPristineState('step4', otcMappingItems);
      // Fetch prices for pre-matched OTC items
      fetchPricesForItems(otcMappingItems);

      // Set OTC options from full list (now uses unified OTCInstrumentItem)
      setIllioOTCOptions(otcListResponse.otcInstruments);
    } catch (error) {
      console.error('Failed to load OTC data:', error);
      // Show error state - clear OTC items
      setOTCMappings([]);
      setIllioOTCOptions([]);
    } finally {
      setStepLoading(false);
    }
    // Note: setItemMappings, setStepLoading, setPristineState are omitted from deps - Zustand store functions are stable
  }, [sessionId]);

  // Load OTC data on mount
  useEffect(() => {
    loadOTCData();
  }, [loadOTCData]);

  // Handle sync OTC from Illio
  const handleSyncOTC = useCallback(async () => {
    setIsSyncing(true);
    try {
      await Actions.syncOTCFromIllio();

      // Reload OTC data after sync
      await loadOTCData();
    } catch (error) {
      console.error('Failed to sync OTC from Illio:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [loadOTCData]);

  // Track initial sort to preserve row position after selection
  const initialSortDone = useRef(false);
  const sortedIndices = useRef<Record<string, number>>({});

  // Transform mappings to DataGrid rows format
  // Using selector, only OTC items are returned
  const rows = useMemo(() => {
    const mappedRows = otcItems.map(mapping => ({
      id: mapping.id,
      isin: mapping.isin,
      symbol: mapping.symbol,
      multiplier: mapping.multiplier,
      original_multiplier: mapping.original_multiplier,
      ...mapping.otc_values, // Spread dynamic OTC values
      instrument_source_id: mapping.instrument_source_id,
      name: mapping.name,
      instrument_type: mapping.instrument_type,
      status: mapping.status,
      account_numbers: mapping.account_numbers,
    }));

    // On first load: sort with disabled-account items at bottom, pending before matched within each group
    if (!initialSortDone.current && mappedRows.length > 0) {
      mappedRows.sort((a, b) => {
        const aDisabled = isItemDisabledAccountOnly(a.account_numbers) ? 1 : 0;
        const bDisabled = isItemDisabledAccountOnly(b.account_numbers) ? 1 : 0;
        if (aDisabled !== bDisabled) return aDisabled - bDisabled;
        if (a.status === 'pending' && b.status === 'matched') return -1;
        if (a.status === 'matched' && b.status === 'pending') return 1;
        return 0;
      });
      mappedRows.forEach((row, index) => {
        sortedIndices.current[row.id] = index;
      });
      initialSortDone.current = true;
      return mappedRows;
    }

    // After initial sort: preserve order using stored indices
    return mappedRows.sort((a, b) => {
      const indexA = sortedIndices.current[a.id] ?? Number.MAX_SAFE_INTEGER;
      const indexB = sortedIndices.current[b.id] ?? Number.MAX_SAFE_INTEGER;
      return indexA - indexB;
    });
  }, [otcItems, isItemDisabledAccountOnly]);

  const { searchTerm, handleSearch: handleTableSearch, filteredData: filteredRows } = useTableSearch(rows, { debounceMs: 300 });

  const handleIllioOTCChange = (
    rowId: string,
    option: OTCInstrumentItem | null
  ) => {
    let multiplierToUse = option?.original_multiplier ?? null;

    // Check if this mapping already exists in other rows - inherit their multiplier
    if (option?.instrument_source_id?.source_id) {
      const existingRow = otcItems.find(
        item =>
          item.id !== rowId &&
          item.instrument_source_id?.source_id ===
          option.instrument_source_id.source_id
      );
      if (existingRow?.multiplier != null) {
        multiplierToUse = existingRow.multiplier;
      }
    }

    updateItemMapping(rowId, {
      instrument_source_id: option
        ? {
          source_id: option.instrument_source_id.source_id ?? null,
          source_data:
            option.instrument_source_id.source_data ?? 'OTCService',
        }
        : null,
      name: option?.name || null,
      currency_code: option?.currency_code || null,
      label: option?.label || null,
      multiplier: multiplierToUse,
      original_multiplier: option?.original_multiplier ?? null,
      instrument_type: option?.instrument_type || null,
      otc_db_id: option?.otc_db_id || null,
    });

    // Fetch or clear price based on selection
    if (option?.instrument_source_id?.source_id && option?.instrument_source_id?.source_data) {
      fetchPrice(rowId, option.instrument_source_id.source_id, option.instrument_source_id.source_data);
    } else {
      clearPrice(rowId);
    }
  };

  const handleRowUpdate = useCallback(
    (newRow: MappingItem, oldRow: MappingItem) => {
      if (newRow.multiplier !== oldRow.multiplier) {
        const coercedMultiplier = coerceValue(newRow.multiplier) as number | null;
        const sourceId = newRow.instrument_source_id?.source_id;
        if (sourceId) {
          // Update multiplier for all rows with the same linked instrument
          otcItems.forEach(item => {
            if (item.instrument_source_id?.source_id === sourceId) {
              updateItemMapping(item.id, { multiplier: coercedMultiplier });
            }
          });
        } else {
          updateItemMapping(newRow.id, { multiplier: coercedMultiplier });
        }
      }
      return newRow;
    },
    [updateItemMapping, otcItems]
  );

  // Handler to move OTC to instruments step - preserves unsaved selections
  const handleMoveToInstruments = useCallback(
    async (otcId: string) => {
      if (isMoving) return;

      const item = otcItems.find(m => m.id === otcId);
      if (!item) return;

      if (!sessionId) {
        console.error('Missing sessionId - cannot move item');
        return;
      }

      setIsMoving(true);
      try {
        // Capture current state before API call (preserves unsaved selections)
        const currentOtcItems = otcItems;

        // Call backend API
        const response = await Actions.moveWizardItem(
          sessionId,
          otcId,
          'instrument'
        );
        const backendData = response as unknown as {
          instrument_mappings: MappingItem[];
          otc_mappings: MappingItem[];
        };

        // Instruments: Use backend response (includes the newly moved item with reset fields)
        setInstrumentMappings(backendData.instrument_mappings);

        // Merge: Keep current unsaved OTC selections, just remove the moved item
        const updatedOtcItems = currentOtcItems.filter(m => m.id !== otcId);
        setOTCMappings(updatedOtcItems);

        // Update pristine state
        setPristineState('step4', updatedOtcItems);
      } catch (error) {
        console.error('Failed to move item to instruments:', error);
      } finally {
        setIsMoving(false);
      }
    },
    [
      isMoving,
      otcItems,
      sessionId,
      setInstrumentMappings,
      setOTCMappings,
      setPristineState,
      setIsMoving,
    ]
  );

  // Memoize validateFields to prevent infinite loops in action registration useEffect
  // Always allow proceeding - validation message shown but doesn't block navigation
  // Backend handles partial mappings gracefully (unmapped items are filtered out)
  const validateFields = useCallback((): boolean => {
    return true;
  }, []);

  // Save OTC mappings to backend
  const handleNext = useCallback(async (): Promise<boolean | number> => {
    // Allow proceeding with unmapped items - backend handles partial mappings gracefully

    if (!sessionId) {
      console.error('Missing sessionId - cannot save OTC mappings');
      return false;
    }

    setIsSaving(true);
    try {
      // Convert store format back to API format (no category field needed)
      const mappingsToSave = otcItems.map(m => ({
        id: m.id,
        otc_values: m.otc_values,
        instrument_source_id: m.instrument_source_id,
        name: m.name,
        currency_code: m.currency_code,
        label: m.label,
        multiplier: m.multiplier,
        original_multiplier: m.original_multiplier,
        instrument_type: m.instrument_type,
        status: m.status,
        isin: m.isin,
        symbol: m.symbol,
        illio_code: m.illio_code,
        otc_db_id: m.otc_db_id,
      }));

      await Actions.saveWizardOTCMappings(sessionId, mappingsToSave);

      // Update pristine state after successful save
      setPristineState('step4', otcItems);

      return true; // Always proceed to Review step
    } catch (error) {
      console.error('Failed to save OTC mappings:', error);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [sessionId, otcItems, setPristineState]);

  // Update button state whenever dependencies change
  // Show informational message about unmapped items but don't block navigation
  useEffect(() => {
    const unmappedCount = otcItems.filter(
      m => !m.instrument_source_id?.source_id
    ).length;

    setCurrentStepButtonState({
      disabled: isMoving,
      loading: isSaving || isMoving,
      validationMessage:
        unmappedCount > 0
          ? `${unmappedCount} OTC instrument${unmappedCount !== 1 ? 's' : ''} remaining to map.`
          : undefined,
    });
  }, [otcItems, isSaving, isMoving]);

  // Register step actions when component mounts
  // Note: registerStepActions and clearStepActions are omitted from deps - Zustand store functions are stable
  useEffect(() => {
    registerStepActions({
      onNext: handleNext,
      validateFields,
    });

    return () => {
      clearStepActions();
    };
  }, [handleNext, validateFields]);

  return {
    rows: filteredRows,
    otcColumns,
    illioOTCOptions,
    handleIllioOTCChange,
    handleMoveToInstruments,
    validateFields,
    isSaving,
    handleSyncOTC,
    isSyncing,
    isMoving,
    handleRowUpdate,
    searchTerm,
    handleTableSearch,
    isItemDisabledAccountOnly,
    prices,
  };
};
